import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BasicInfo, PortfolioData } from "../types";
import {
  formatObjective,
  calculateYearsOfExperience,
} from "../utils/experienceCalculator";
import { scrollToSection } from "../utils/scrollToSection";
import { generateResumePDF } from "../utils/generateResumePDF";

interface HeaderProps {
  basics: BasicInfo;
  portfolioData?: PortfolioData;
}

const Header: React.FC<HeaderProps> = ({ basics, portfolioData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleDownloadResume = () => {
    if (portfolioData) {
      generateResumePDF(portfolioData);
    }
  };

  const handleCardClick = (sectionId: string) => {
    if (sectionId === "projects") {
      // Navigate to the dedicated projects page and scroll to top
      navigate("/projects");
      // Ensure we scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      // For experience and skills, scroll to sections on home page
      if (location.pathname !== "/" && location.pathname !== "/home") {
        navigate(`/home#${sectionId}`);
      } else {
        scrollToSection(`#${sectionId}`);
      }
    }
  };

  return (
    <header className="site-header">
      {/* Animated Background Elements */}
      <div className="header-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
        <div className="gradient-overlay"></div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-lg-12 col-12 d-flex flex-wrap align-items-center">
            <div className="profile-thumb">
              <div className="profile-image-container">
                <img
                  src="/assets/profile.webp"
                  className="profile-image"
                  alt={basics.name}
                  fetchPriority="high"
                  loading="eager"
                  width="320"
                  height="320"
                  sizes="(max-width: 768px) 280px, 320px"
                />
                <div className="profile-glow"></div>
                <div className="profile-badge">
                  <i className="fas fa-code"></i>
                </div>
              </div>
            </div>

            <div className="profile-info">
              <h1 className="hero-name">
                I'm <span className="highlight-name">{basics.name}</span>
              </h1>

              <div className="title-container">
                <em className="hero-title">{basics.label}</em>
                <div className="title-underline"></div>
              </div>

              <p className="hero-description">
                {formatObjective(basics.objective)}
              </p>

              <div className="hero-stats">
                <div
                  className="stat-card clickable"
                  onClick={() => handleCardClick("experience")}
                  title="Click to view experience"
                >
                  <div className="stat-icon">
                    <i className="fas fa-briefcase"></i>
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">
                      {calculateYearsOfExperience()}+
                    </span>
                    <span className="stat-label">Years Experience</span>
                  </div>
                </div>
                <div
                  className="stat-card clickable"
                  onClick={() => handleCardClick("projects")}
                  title="Click to view all projects"
                >
                  <div className="stat-icon">
                    <i className="fas fa-project-diagram"></i>
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">9+</span>
                    <span className="stat-label">Projects</span>
                  </div>
                </div>
                <div
                  className="stat-card clickable"
                  onClick={() => handleCardClick("skills")}
                  title="Click to view skills"
                >
                  <div className="stat-icon">
                    <i className="fas fa-tools"></i>
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">25+</span>
                    <span className="stat-label">Skills</span>
                  </div>
                </div>
              </div>

              <div className="social-links">
                {basics.profiles.map((profile, index) => (
                  <a
                    key={index}
                    href={profile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={`Visit ${basics.name}'s ${profile.network} profile`}
                  >
                    <i
                      className={`fab fa-${profile.network.toLowerCase()}`}
                      aria-hidden="true"
                    ></i>
                  </a>
                ))}
              </div>

              <div className="cta-buttons">
                <a href="/home#experience" className="btn btn-primary">
                  <i className="fas fa-briefcase"></i>
                  <span> Experience</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
                <a href="/projects" className="btn btn-primary">
                  <i className="fas fa-folder-open"></i>
                  <span> Projects</span>
                  <i className="fas fa-external-link-alt"></i>
                </a>
                {portfolioData && (
                  <button
                    onClick={handleDownloadResume}
                    className="btn btn-primary"
                    type="button"
                    aria-label="Download Resume as PDF"
                  >
                    <i className="fas fa-download"></i>
                    <span> Download Resume</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
